import AiColumn from "./AiColumn";

// seconds -> "hh:mm:ss" / "mm:ss" / "Ns"
const formatDuration = (seconds) => {
  const totalSeconds = parseInt(seconds || 0, 10);
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  if (hrs > 0) return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  if (mins > 0) return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  return `${secs}s`;
};

// GHL sends ISO with an explicit "Z" (UTC) — safe to parse directly with `new Date()`.
const toDateOrNull = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

// Kixie sends a NAIVE "YYYY-MM-DD HH:mm:ss.uuuuuu" string with no timezone marker.
// ASSUMPTION (unverified): that string is UTC. Verify against a call you remember making —
// if it's off by a fixed number of hours, this assumption is wrong; see note below.
const parseKixieDate = (value) => {
  if (!value) return null;
  const isoish = value.trim().replace(" ", "T");
  const withMs = isoish.replace(/(\.\d{3})\d*$/, "$1"); // JS Date only supports ms precision
  const d = new Date(withMs.endsWith("Z") ? withMs : `${withMs}Z`);
  return isNaN(d.getTime()) ? null : d;
};

// If it turns out Kixie's calldate is ALREADY New York local time (not UTC),
// swap parseKixieDate's usages below for this instead:
// const parseKixieDateAsNY = (value) => {
//   if (!value) return null;
//   const isoish = value.trim().replace(" ", "T").replace(/(\.\d{3})\d*$/, "$1");
//   // Treat the naive string as NY wall-clock time, find its correct UTC instant.
//   const asUTCGuess = new Date(`${isoish}Z`);
//   const nyOffsetMs = asUTCGuess.getTime() - new Date(asUTCGuess.toLocaleString("en-US", { timeZone: "America/New_York" })).getTime();
//   return new Date(asUTCGuess.getTime() + nyOffsetMs);
// };

const formatNYTime = (date, options = {}) => {
  if (!date) return "";
  return date.toLocaleString("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    // timeZoneName: "short", // adds "EDT" / "EST"
    ...options,
  });
};

export function buildGhlGridColumns({ generating, onView, onGenerate }) {
  return [
    { field: "name", headerName: "Name", width: 160, valueGetter: (value, row) => row.fullName || row.contactName || "" },
    { field: "phone", headerName: "Phone", width: 140, valueGetter: (value, row) => row.call_to || "" },
    { field: "agent", headerName: "Agent", width: 140, valueGetter: (value, row) => row.user_name || "Unknown" },
    { field: "first_time_call", headerName: "First Time", width: 110, valueGetter: (value, row) => row.first_time_call || "No" },
    { field: "type", headerName: "Type", width: 110, valueGetter: (value, row) => (row.direction === "inbound" ? "Incoming" : "Outgoing") },
    { field: "call_status", headerName: "Status", width: 120 },
    { field: "call_disposition", headerName: "Disposition", width: 140 },
    {
      field: "call_duration",
      headerName: "Duration",
      width: 110,
      type: "number",
      valueGetter: (value, row) => Number(row.call_duration || 0),
      renderCell: (params) => formatDuration(params.value),
    },
    {
      field: "call_start_time",
      headerName: "Date",
      width: 200,
      type: "dateTime",
      valueGetter: (value, row) => toDateOrNull(row.call_start_time), // keep as Date for sort/filter
      renderCell: (params) => formatNYTime(params.value), // format only for display
    },
    {
      field: "recording",
      headerName: "Recording",
      width: 240,
      sortable: false,
      filterable: false,
      renderCell: (params) => (params.row.call_duration > 0 && params.row.recording_url ? <audio controls src={params.row.recording_url} style={{ width: "100%", height: 28 }} /> : "N/A"),
    },
    {
      field: "ai",
      headerName: "Enhance Transcript",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => <AiColumn call={params.row} source="ghl" generating={generating} onView={onView} onGenerate={onGenerate} getRecordingUrl={(c) => c.recording_url} />,
    },
  ];
}

export function buildKixieGridColumns({ generating, onView, onGenerate }) {
  return [
    {
      field: "name",
      headerName: "Name",
      width: 160,
      valueGetter: (value, row) => `${row.kixiePowerlistContactDetails?.firstName || ""} ${row.kixiePowerlistContactDetails?.lastName || ""}`.trim(),
    },
    { field: "phone", headerName: "Phone", width: 140, valueGetter: (value, row) => row.phone || row.kixieCallDetails?.tonumber || "" },
    { field: "agent", headerName: "Agent", width: 140, valueGetter: (value, row) => row.kixieCallDetails?.calleridName || "Unknown" },
    { field: "first_time_call", headerName: "First Time", width: 110, valueGetter: (value, row) => row.first_time_call || "No" },
    { field: "type", headerName: "Type", width: 110, valueGetter: (value, row) => row.calltype || row.kixieCallDetails?.calltype || "" },
    { field: "call_status", headerName: "Status", width: 120, valueGetter: (value, row) => row.kixieCallDetails?.callstatus || "" },
    { field: "disposition", headerName: "Disposition", width: 140, valueGetter: (value, row) => row.disposition || "" },
    {
      field: "duration",
      headerName: "Duration",
      width: 110,
      type: "number",
      valueGetter: (value, row) => Number(row.kixieCallDetails?.duration || 0),
      renderCell: (params) => formatDuration(params.value),
    },
    {
      field: "calldate",
      headerName: "Date",
      width: 200,
      type: "dateTime",
      valueGetter: (value, row) => parseKixieDate(row.kixieCallDetails?.calldate), // keep as Date
      renderCell: (params) => formatNYTime(params.value),
    },
    {
      field: "recording",
      headerName: "Recording",
      width: 240,
      sortable: false,
      filterable: false,
      renderCell: (params) => (params.row.kixieCallDetails?.duration > 0 && params.row.kixieCallDetails?.recordingurl ? <audio controls src={params.row.kixieCallDetails.recordingurl} style={{ width: "100%", height: 28 }} /> : "N/A"),
    },
    {
      field: "ai",
      headerName: "Enhance Transcript",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => <AiColumn call={params.row} source="kixie" generating={generating} onView={onView} onGenerate={onGenerate} getRecordingUrl={(c) => c.kixieCallDetails?.recordingurl} />,
    },
  ];
}
